import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useLocation, useParams } from 'react-router-dom';
import { pushEvent } from '../../utils/analytics';
import { fetchCatalog } from '../../api/checkout';
import { getApiBaseUrl } from '../../utils/apiBaseUrl';
import { fetchBootcampOfferSummary, formatBootcampOfferDate } from './challengePageUtils';
import './ChallengePageClarity.css';
import './ChallengeBookingPage.css';

const BOOTCAMP_PATH = '/courses/ai-trading-bootcamp';

function readCallContext(slug, state) {
  if (state?.callContext?.slug === slug) return state.callContext;
  try {
    const saved = JSON.parse(window.sessionStorage.getItem('challengeCallContext') || '{}');
    return saved.slug === slug ? saved : {};
  } catch {
    return {};
  }
}

function appointmentTime(value, timezone) {
  const date = new Date(value);
  if (!value || !Number.isFinite(date.getTime())) return null;
  try {
    return date.toLocaleString('en-US', {
      dateStyle: 'full',
      timeStyle: 'short',
      timeZone: timezone,
    });
  } catch {
    return (
      date.toLocaleString('en-US', { dateStyle: 'full', timeStyle: 'short', timeZone: 'UTC' }) +
      ' UTC'
    );
  }
}

export default function ChallengeCallConfirmedPage() {
  const { slug } = useParams();
  const location = useLocation();
  const callContext = useMemo(() => readCallContext(slug, location.state), [slug, location.state]);
  const [booking, setBooking] = useState({
    status: callContext.readToken ? 'loading' : 'unverified',
  });
  const [verificationAttempt, setVerificationAttempt] = useState(0);
  const [bootcampOffer, setBootcampOffer] = useState(null);
  const [tuitionWindow, setTuitionWindow] = useState(null);
  const [offerLoaded, setOfferLoaded] = useState(false);

  useEffect(() => {
    if (!callContext.readToken) {
      setBooking({ status: 'unverified' });
      return undefined;
    }
    let active = true;
    let timer;
    const abort = new AbortController();
    const deadline = window.setTimeout(() => {
      abort.abort();
      if (active) setBooking({ status: 'unverified' });
    }, 20000);
    setBooking({ status: 'loading' });
    const verify = async (attempt = 0) => {
      try {
        const response = await fetch(
          `${getApiBaseUrl() || 'https://api.botspot.trade'}/calendly/booking`,
          {
            headers: { Authorization: `Bearer ${callContext.readToken}` },
            signal: abort.signal,
            cache: 'no-store',
          },
        );
        if (!response.ok) throw new Error('verification_unavailable');
        const result = await response.json();
        if (!active) return;
        if (result.status === 'pending' && attempt < 7) {
          timer = window.setTimeout(() => verify(attempt + 1), 1500);
          return;
        }
        window.clearTimeout(deadline);
        setBooking(
          ['scheduled', 'canceled', 'pending'].includes(result.status)
            ? result
            : { status: 'unverified' },
        );
      } catch {
        window.clearTimeout(deadline);
        if (active) setBooking({ status: 'unverified' });
      }
    };
    verify();
    return () => {
      active = false;
      abort.abort();
      window.clearTimeout(timer);
      window.clearTimeout(deadline);
    };
  }, [callContext.readToken, verificationAttempt]);

  useEffect(() => {
    pushEvent({
      event: 'expert_call_booking_success_page_viewed',
      source: window.location.pathname,
      referrer: document.referrer || 'calendly_embed',
    });
  }, []);

  useEffect(() => {
    let mounted = true;
    const loadOffer = async () => {
      try {
        const summary = await fetchBootcampOfferSummary(fetchCatalog);
        if (!mounted) return;
        setBootcampOffer({
          name: summary.name,
          cohortName: summary.cohortName,
          startsAt: summary.startsAt,
        });
        setTuitionWindow(summary.tuitionWindow);
      } catch {
        if (!mounted) return;
        setBootcampOffer(null);
        setTuitionWindow(null);
      } finally {
        if (mounted) setOfferLoaded(true);
      }
    };
    loadOffer();
    return () => {
      mounted = false;
    };
  }, []);

  const nextCohortDate = useMemo(
    () => formatBootcampOfferDate(bootcampOffer?.startsAt),
    [bootcampOffer?.startsAt],
  );
  const enrollmentEnds = useMemo(
    () => formatBootcampOfferDate(tuitionWindow?.endDate),
    [tuitionWindow?.endDate],
  );

  return (
    <main
      className="challenge-next challenge-next--confirmed challenge-clarity-review"
      data-testid="challenge-call-confirmed-page"
      data-education-design-variant="clarity"
      data-education-theme="light"
      data-experiment-active="false"
    >
      <Helmet>
        <title>Your call details | BotSpot</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <div className="challenge-next__confirmation-layout">
        <section className="challenge-next__appointment" aria-label="Appointment details">
          <div aria-live="polite" className="challenge-next__receipt">
            <p className="challenge-next__receipt-label">Your free setup call</p>
            <h1>
              {booking.status === 'scheduled'
                ? "You're booked. Talk soon."
                : booking.status === 'loading'
                  ? 'Verifying your appointment…'
                  : booking.status === 'canceled'
                    ? booking.rescheduled
                      ? 'This appointment was rescheduled.'
                      : 'This appointment was canceled.'
                    : "We couldn't verify this appointment yet."}
            </h1>
            {booking.status === 'scheduled' ? (
              <>
                {appointmentTime(booking.startsAt, booking.timezone) ? (
                  <p className="challenge-next__appointment-time">
                    <strong>{appointmentTime(booking.startsAt, booking.timezone)}</strong>
                    <small>{(booking.timezone || 'UTC').replaceAll('_', ' ')}</small>
                  </p>
                ) : (
                  <p>Your confirmation email has the appointment time.</p>
                )}
                {booking.hosts?.length ? (
                  <p className="challenge-next__host">With {booking.hosts.join(' and ')}</p>
                ) : (
                  <p className="challenge-next__host">With the Lumiwealth team</p>
                )}
                {booking.joinUrl ? (
                  <div className="challenge-next__join-action">
                    <a
                      className="challenge-next__secondary"
                      href={booking.joinUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Join your call
                    </a>
                    <span>At your scheduled time</span>
                  </div>
                ) : (
                  <p>Your calendar invitation contains the joining instructions.</p>
                )}
                <div className="challenge-next__appointment-tools">
                  {booking.rescheduleUrl ? (
                    <a
                      className="challenge-next__utility"
                      href={booking.rescheduleUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Reschedule
                    </a>
                  ) : null}
                  {booking.cancelUrl ? (
                    <a
                      className="challenge-next__utility"
                      href={booking.cancelUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Cancel appointment
                    </a>
                  ) : null}
                </div>
              </>
            ) : booking.status !== 'loading' ? (
              <>
                <p>
                  {booking.status === 'canceled'
                    ? 'Check your latest Calendly confirmation for updated details.'
                    : 'This page does not book a call. Just booked? Your confirmation may still be arriving.'}
                </p>
                <div className="challenge-next__actions">
                  {callContext.readToken ? (
                    <button
                      className="challenge-next__secondary"
                      type="button"
                      onClick={() => setVerificationAttempt((attempt) => attempt + 1)}
                    >
                      Check again
                    </button>
                  ) : null}
                  <Link
                    className="challenge-next__primary"
                    to={`/challenges/${slug}/welcome#book-call`}
                  >
                    Return to booking
                  </Link>
                </div>
              </>
            ) : null}
          </div>
          {booking.status === 'scheduled' ? (
            <p className="challenge-next__prep">
              Bring one idea and one question. We’ll work from there.
            </p>
          ) : null}
        </section>
        <section className="challenge-next__course" aria-labelledby="challenge-offer-title">
          <div className="challenge-next__course-heading">
            <img
              src="/images/challenges/guided-research.png"
              alt="A decision-tree notebook and magnifying glass for guided research"
              width="1024"
              height="1024"
            />
            <div>
              <p className="challenge-next__course-label">The live AI Trading Bootcamp</p>
              <h2 id="challenge-offer-title">Build your own AI trading workflow.</h2>
            </div>
          </div>
          <p className="challenge-next__course-benefit">
            Go from scattered tools to a research process you understand, with live instructor
            guidance.
          </p>
          <Link
            className="challenge-next__primary"
            to={`${BOOTCAMP_PATH}?utm_source=challenge_call_confirmed&utm_medium=booking&utm_campaign=ai_trading_challenge`}
          >
            Explore the Bootcamp <span aria-hidden="true">→</span>
          </Link>
          <p className="challenge-next__course-hint">See the curriculum, dates &amp; tuition</p>
          {offerLoaded ? (
            <>
              <div className="challenge-next__offer-facts" aria-label="Current Bootcamp details">
                {nextCohortDate ? <span>Next cohort: {nextCohortDate}</span> : null}
                {enrollmentEnds ? (
                  <span>
                    {tuitionWindow.label || 'Enrollment'} ends {enrollmentEnds}
                  </span>
                ) : null}
              </div>
            </>
          ) : (
            <p className="challenge-next__status">Loading current dates…</p>
          )}
        </section>
      </div>
      <Link className="challenge-next__skip" to={`/challenges/${slug}`}>
        Back to the challenge
      </Link>
    </main>
  );
}
